import { useEffect } from 'react';
import { createAutosaver } from '../../io/autosave';
import { useEditorStore } from '../../state/editorStore';

/**
 * Keep the working document in IndexedDB so a refresh or a crash does not lose
 * work, and offer it back on the next visit.
 *
 * Restoring is deliberately conservative: it only applies if the user has not
 * touched the document in the meantime, so a slow disk never clobbers work.
 */
export function useAutosave(): void {
  useEffect(() => {
    const initialDocId = useEditorStore.getState().doc.id;
    const saver = createAutosaver({
      getDoc: () => useEditorStore.getState().doc,
      onError: () =>
        useEditorStore.getState().notify('Autosave is unavailable in this browser', 'warn'),
    });

    saver.start();

    const unsubscribe = useEditorStore.subscribe((state, previous) => {
      if (state.revision !== previous.revision) saver.schedule();
    });

    const flushOnHide = () => void saver.flush();
    window.addEventListener('pagehide', flushOnHide);

    void saver.load().then((doc) => {
      if (!doc) return;
      const store = useEditorStore.getState();
      // Painting keeps the document id, so the id alone cannot tell an
      // untouched document from one the user already drew on while the disk
      // was being read. revision is 0 until the first commit/new/load, and
      // any edit in this window bumps it — restoring then would clobber it.
      if (store.doc.id !== initialDocId || store.revision !== 0) return;
      store.loadDocument(doc);
      store.zoomToFit();
      store.notify(`Restored "${doc.name}" from your last session`);
    });

    return () => {
      unsubscribe();
      saver.stop();
      window.removeEventListener('pagehide', flushOnHide);
    };
  }, []);
}
